import cx from "classnames";
import React, { forwardRef, useCallback, useRef } from "react";
import { useMergeRef } from "@vibe/shared";
import { CloseSmall as CloseSmallIcon, Search as SearchIcon } from "@vibe/icons";
import { ComponentDefaultTestId, getTestId } from "../../tests/test-ids-utils";
import styles from "./Search.module.scss";
import { BaseInput } from "@vibe/base";
import useDebounceEvent from "../../hooks/useDebounceEvent";
import { IconButton } from "@vibe/icon-button";
import { Icon } from "@vibe/icon";
import { type SearchProps } from "./Search.types";
import { Loader } from "@vibe/loader";
import { ComponentVibeId } from "../../tests/constants";

const Search = forwardRef(
  (
    {
      searchIconName = SearchIcon,
      clearIconName = CloseSmallIcon,
      clearIconLabel = "Clear",
      renderAction: RenderAction,
      hideRenderActionOnInput,
      value,
      placeholder,
      size = "medium",
      disabled,
      loading,
      autoFocus,
      autoComplete = "off",
      inputAriaLabel,
      debounceRate = 400,
      searchResultsContainerId,
      currentAriaResultId,
      onChange,
      onFocus,
      onBlur,
      onClear,
      onKeyDown,
      className,
      "aria-expanded": ariaExpanded,
      "aria-haspopup": ariaHasPopup,
      showClearIcon = true,
      id,
      "data-testid": dataTestId
    }: SearchProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const inputRef = useRef(null);
    const mergedRef = useMergeRef(ref, inputRef);

    const { inputValue, onEventChanged, clearValue } = useDebounceEvent({
      delay: debounceRate,
      onChange,
      initialStateValue: value
    });

    const onClearButtonClick = useCallback(() => {
      if (disabled) return;
      inputRef.current?.focus?.();
      clearValue();
      onClear?.();
    }, [disabled, clearValue, onClear]);

    const SearchIcon = (
      <Icon icon={searchIconName} className={styles.icon} type="font" size={size === "small" ? "16px" : "20px"} />
    );

    const ClearIcon = (
      <IconButton
        className={cx(styles.icon, { [styles.empty]: !inputValue })}
        icon={clearIconName}
        aria-label={clearIconLabel}
        onClick={onClearButtonClick}
        size={size === "small" ? "xs" : "small"}
        data-testid={getTestId(ComponentDefaultTestId.CLEAN_SEARCH_BUTTON, id)}
      />
    );

    const RenderRight = (
      <>
        {loading && (
          <Loader
            size={size === "small" ? "xs" : 20}
            color="secondary"
            wrapperClassName={cx({ [styles.loader]: !inputValue && !RenderAction })}
          />
        )}
        {showClearIcon && inputValue && !disabled && ClearIcon}
        {!(hideRenderActionOnInput && inputValue) && RenderAction}
      </>
    );

    return (
      <BaseInput
        ref={mergedRef}
        id={id}
        type={"search"}
        data-testid={dataTestId || getTestId(ComponentDefaultTestId.SEARCH, id)}
        data-vibe={ComponentVibeId.SEARCH}
        className={cx(styles.searchWrapper, className)}
        inputClassName={styles.search}
        value={inputValue}
        renderLeft={SearchIcon}
        renderRight={RenderRight}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onEventChanged}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        size={size}
        wrapperRole="search"
        inputRole={searchResultsContainerId ? "combobox" : undefined}
        aria-label={inputAriaLabel}
        aria-busy={loading}
        aria-controls={ariaExpanded ? searchResultsContainerId : undefined}
        aria-activedescendant={currentAriaResultId}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
      />
    );
  }
);

export default Search;
